import { Badge } from "@thilke/ui/components/badge";
import { ScrollArea } from "@thilke/ui/components/scroll-area";
import { cn } from "@thilke/ui/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

export interface RollLogEntry {
  id: string;
  expression: string;
  result: number;
  breakdown?: string;
  rolls?: number[];
  speaker?: string;
  when?: number;
  visibility?: "public" | "gm-only";
}

const rollLevelVariants = cva("uppercase", {
  variants: {
    tone: {
      default: "text-muted-foreground",
      crit: "text-success",
      fumble: "text-destructive",
      warning: "text-warning",
    },
  },
  defaultVariants: {
    tone: "default",
  },
});

type RollLevelTone = NonNullable<
  VariantProps<typeof rollLevelVariants>["tone"]
>;

function classifyRoll(expression: string, rolls: number[] = []): RollLevelTone {
  const lower = expression.toLowerCase();
  if (lower.includes("d20")) {
    if (rolls.includes(20)) return "crit";
    if (rolls.includes(1)) return "fumble";
  }
  return "default";
}

function formatWhen(ms: number): string {
  const diff = Math.max(0, Date.now() - ms);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  return `${Math.floor(min / 60)}h`;
}

export interface RollLogBlockProps
  extends Omit<React.ComponentProps<"section">, "children"> {
  entries?: RollLogEntry[];
  live?: boolean;
  showSpeaker?: boolean;
  maxHeight?: string;
  onEntryClick?: (id: string) => void;
}

function RollLogBlock({
  className,
  entries = [],
  live = true,
  showSpeaker = true,
  maxHeight,
  onEntryClick,
  ...props
}: RollLogBlockProps) {
  const displayEntries = entries.slice().reverse();
  const scrollStyle = maxHeight
    ? ({ maxHeight } as React.CSSProperties)
    : undefined;

  return (
    <section
      data-slot="roll-log-block"
      className={cn("rounded-lg border bg-card p-3", className)}
      {...props}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">Roll results</h3>
          <p className="text-xs text-muted-foreground">
            Global dice roll feed.
          </p>
        </div>
        <Badge variant={live ? "default" : "outline"}>
          {live ? "live" : "idle"}
        </Badge>
      </div>
      <ScrollArea
        className={cn(
          "rounded-md bg-background p-3 font-mono text-xs",
          !maxHeight && "h-44",
        )}
        style={scrollStyle}
      >
        <div className="grid gap-1">
          {displayEntries.length === 0 ? (
            <div className="text-muted-foreground italic">No rolls yet.</div>
          ) : (
            displayEntries.map((entry) => {
              const tone = classifyRoll(entry.expression, entry.rolls);
              const speakerLabel =
                showSpeaker && entry.speaker ? entry.speaker : null;
              const timeLabel = entry.when ? formatWhen(entry.when) : null;
              const metaLabel = [speakerLabel, timeLabel]
                .filter(Boolean)
                .join(" · ");

              const Tag = onEntryClick ? "button" : "div";
              return (
                <Tag
                  key={entry.id}
                  data-slot="roll-log-entry"
                  className={cn(
                    "w-full grid grid-cols-[52px_1fr_auto] gap-2 rounded-sm px-1 py-0.5 text-left",
                    onEntryClick && "cursor-pointer hover:bg-muted/30",
                    tone === "crit" && "bg-success/5",
                    tone === "fumble" && "bg-destructive/5",
                  )}
                  type={onEntryClick ? "button" : undefined}
                  onClick={() => onEntryClick?.(entry.id)}
                >
                  <span className={cn(rollLevelVariants({ tone }))}>
                    {tone === "crit"
                      ? "crit"
                      : tone === "fumble"
                        ? "fumb"
                        : entry.visibility === "gm-only"
                          ? "gm"
                          : "roll"}
                  </span>
                  <span className="min-w-0">
                    <span className="font-semibold text-foreground">
                      {entry.expression}
                    </span>
                    {entry.breakdown ? (
                      <span className="text-muted-foreground">
                        {" "}
                        → {entry.breakdown}
                      </span>
                    ) : null}
                    {metaLabel ? (
                      <span className="ml-1 text-[10px] text-muted-foreground">
                        ({metaLabel})
                      </span>
                    ) : null}
                  </span>
                  <span
                    className={cn(
                      "font-bold",
                      tone === "crit" && "text-success",
                      tone === "fumble" && "text-destructive",
                      tone !== "crit" && tone !== "fumble" && "text-foreground",
                    )}
                  >
                    {entry.result}
                  </span>
                </Tag>
              );
            })
          )}
        </div>
      </ScrollArea>
    </section>
  );
}

export { classifyRoll, type RollLevelTone, RollLogBlock, rollLevelVariants };
